import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { CloseBold } from '@openai/apps-sdk-ui/components/Icon';

export function Modal({
  title,
  children,
  close,
  className = '',
}: {
  title: string;
  children: ReactNode;
  close: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current!;
    dialog.showModal();
    dialog
      .querySelector<HTMLElement>(
        'input:not([disabled]):not([readonly]), textarea:not([disabled]), select:not([disabled])',
      )
      ?.focus({ preventScroll: true });
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`modal ${className}`}
      aria-label={title}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="modal-inner">
        <div className="modal-heading">
          <h2>{title}</h2>
          <Button
            color="secondary"
            variant="ghost"
            size="sm"
            uniform
            aria-label="Close dialog"
            onClick={close}
          >
            <CloseBold />
          </Button>
        </div>
        {children}
      </div>
    </dialog>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Empty({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <h2>{title}</h2>
      <p>{description}</p>
      {children && <div className="empty-actions">{children}</div>}
    </div>
  );
}
export function ErrorNotice({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="error-notice" role="alert">
      <span>{message}</span>
      {retry && (
        <Button color="danger" variant="ghost" size="sm" onClick={retry}>
          Try again
        </Button>
      )}
    </div>
  );
}
// Members keep a stable colour by join order (the workspace roster is sorted by
// created_at). Warning and danger tones are left for overdue and priority cues.
const ASSIGNEE_TONES = ['info', 'discovery', 'success'] as const;
export type AssigneeTone = (typeof ASSIGNEE_TONES)[number];
export function assigneeTone(
  members: { id: string }[],
  id: string | null | undefined,
): AssigneeTone | undefined {
  const index = id ? members.findIndex((member) => member.id === id) : -1;
  return index < 0 ? undefined : ASSIGNEE_TONES[index % ASSIGNEE_TONES.length];
}
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}
export function formatTime(value: string) {
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
export function formatDate(value: string) {
  return new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
  });
}
